import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Card, CardBody, CardHeader, Col, Row, Spinner } from "reactstrap";
import PanelHeader from "components/PanelHeader/PanelHeader.js";
import { BASE_URL } from "utils/constants";

const today = new Date().toISOString().split("T")[0];

const defaultFromDate = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().split("T")[0];
};

const statusClassName = (status) => {
  switch (`${status}`) {
    case "0": return "badge bg-danger";
    case "1": return "badge bg-success";
    case "2": return "badge bg-info";
    case "3": return "badge bg-warning text-dark";
    case "4": return "badge bg-secondary";
    default: return "badge bg-light text-dark";
  }
};

function DailyAttendance() {
  const [fromDate, setFromDate] = useState(defaultFromDate());
  const [toDate, setToDate] = useState(today);
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BASE_URL}/student/getdailyattendance.php`, {
      params: { from: fromDate, to: toDate },
    }).then((response) => {
      setSummary(response.data.data.summary);
      setRecords(response.data.data.records || []);
    }).catch((error) => {
      setSummary(null);
      setRecords([]);
      Swal.fire(error.response?.data?.msg || "Unable to load daily attendance", "", "error");
    }).finally(() => setLoading(false));
  }, [fromDate, toDate]);

  return (
    <>
      <PanelHeader size="sm" />
      <div className="content">
        <Card>
          <CardHeader><h4 className="title">Daily Attendance</h4></CardHeader>
          <CardBody>
            <Row className="mb-3">
              <Col md={3} sm={6} className="mb-2">
                <label htmlFor="attendance-from">From</label>
                <input id="attendance-from" className="form-control" type="date"
                  value={fromDate} max={toDate}
                  onChange={(event) => setFromDate(event.target.value)} />
              </Col>
              <Col md={3} sm={6} className="mb-2">
                <label htmlFor="attendance-to">To</label>
                <input id="attendance-to" className="form-control" type="date"
                  value={toDate} min={fromDate}
                  onChange={(event) => setToDate(event.target.value)} />
              </Col>
            </Row>

            {loading ? (
              <div className="text-center py-5"><Spinner color="info" /></div>
            ) : (
              <>
                {summary && (
                  <Row className="mb-3">
                    {[["Total", summary.total], ["Attended", summary.attended],
                      ["Absent", summary.absent], ["Late", summary.late],
                      ["Excused", summary.excused]].map(([label, value]) => (
                      <Col lg md={4} xs={6} className="mb-2" key={label}>
                        <div className="border rounded text-center p-2 h-100">
                          <div className="font-weight-bold" style={{ fontSize: "20px" }}>{value}</div>
                          <small>{label}</small>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}

                <div className="table-responsive">
                  <table className="table table-striped table-bordered mb-0">
                    <thead><tr>
                      <th>Date</th><th>Status</th><th>Entry</th><th>Leave</th>
                      <th>Guardian</th><th>Excuse</th>
                    </tr></thead>
                    <tbody>
                      {records.map((record) => (
                        <tr key={record.id}>
                          <td>{record.attendance_date}</td>
                          <td><span className={statusClassName(record.status)}>{record.status_label}</span></td>
                          <td>{record.entrytime || "-"}</td>
                          <td>{record.leavetime || "-"}</td>
                          <td>{record.guardianname?.trim() || record.otherguardian || "-"}</td>
                          <td>{record.excuse_ids
                            ? `${record.excuse_status || ""}${record.excuse_description ? ` - ${record.excuse_description}` : ""}`
                            : "-"}</td>
                        </tr>
                      ))}
                      {!records.length && (
                        <tr><td colSpan="6" className="text-center py-4">No attendance records in this period.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </div>
    </>
  );
}

export default DailyAttendance;
